import type { ISwipeActionProps } from '@sandstreamdev/react-swipeable-list';
import { SwipeableListItem } from '@sandstreamdev/react-swipeable-list';
import { Box, Icon, Paragraph } from '@zillow/constellation-10';
import { IconArchiveFilled, IconConcealFilled, IconRestoreMailFilled } from '@zillow/constellation-icons';
import { css } from '@/styled-system/css';
import { useCallback, useContext, useMemo, useState } from 'react';
import {
  MultiBrandSupportUserReportConversationReasonType,
  SatelliteConversationFolderTypeV2,
} from '@zg-rentals/ts-satellite-client';
import StringConstants from '../../../constants/StringConstants';
import useConversationActions from '../../../hooks/useConversationActions';
import { InboxPageContext } from '../../../contexts/InboxPageContextProvider';
import stringUtils from '../../../utils/stringUtils';

type SwipeObjectForFolder = {
  text: string;
  icon: React.ReactNode;
};

const swipeContainerStyles = css.raw({
  alignItems: 'center',
  display: 'flex',
  height: '100%',
  width: '100%',
  paddingLeft: 'default',
  paddingRight: 'default',
});

const MOBILE_SWIPE_RIGHT_CONTENT_MAP: Record<SatelliteConversationFolderTypeV2, SwipeObjectForFolder | null> = {
  [SatelliteConversationFolderTypeV2.ARCHIVED]: {
    text: StringConstants.ACTIONS.MOVE_TO_INBOX,
    icon: <Icon render={<IconRestoreMailFilled />} size="sm" />,
  },
  [SatelliteConversationFolderTypeV2.SPAM]: {
    text: StringConstants.ACTIONS.NOT_SPAM,
    icon: <Icon render={<IconRestoreMailFilled />} size="sm" />,
  },
  [SatelliteConversationFolderTypeV2.INBOX]: {
    text: StringConstants.ACTIONS.MARK_UNREAD,
    icon: <Icon render={<IconConcealFilled />} size="sm" />,
  },
  // Unused, must include all Folder options to use as a key
  [SatelliteConversationFolderTypeV2.SHOW_ALL]: null,
  [SatelliteConversationFolderTypeV2.FAVORITE]: null,
};

type Props = {
  children: React.ReactNode;
  conversationId: string;
};

export default function SwipeableWrapper({ children, conversationId }: Props) {
  const [isBlurred, setIsBlurred] = useState(false);
  const { folder } = useContext(InboxPageContext);
  const { archiveConversation, undoArchiveConversation, undoReportConversation, markConversationAsUnread } =
    useConversationActions();

  const handleArchive = useCallback(() => {
    archiveConversation({ conversationIdToUpdate: conversationId });
    setIsBlurred(true);
  }, [archiveConversation, conversationId]);

  /**
   * swipeLeft action only when on SatelliteConversationFolderTypeV2.INBOX
   */
  const swipeLeft: ISwipeActionProps | undefined = useMemo(() => {
    if (folder !== SatelliteConversationFolderTypeV2.INBOX) {
      return undefined;
    }

    return {
      content: (
        <Box
          css={{
            ...swipeContainerStyles,
            justifyContent: 'end',
            backgroundColor: 'gray.300',
            '& svg': {
              marginLeft: 'auto',
            },
          }}
        >
          <Icon render={<IconArchiveFilled />} size="sm" />
          <Paragraph textStyle="body-xs" css={{ marginLeft: 'tighter' }}>
            {stringUtils.capitalize(StringConstants.ACTIONS.ARCHIVE)}
          </Paragraph>
        </Box>
      ),
      action: handleArchive,
    };
  }, [folder, handleArchive]);

  const swipeRightAction = useCallback(() => {
    switch (folder) {
      case SatelliteConversationFolderTypeV2.ARCHIVED:
        undoArchiveConversation({ conversationIdToUpdate: conversationId });
        setIsBlurred(true);
        return;
      case SatelliteConversationFolderTypeV2.SPAM:
        undoReportConversation({
          conversationIdToUpdate: conversationId,
          reportReasonType: MultiBrandSupportUserReportConversationReasonType.NOT_REPORTED,
        });
        setIsBlurred(true);
        return;
      case SatelliteConversationFolderTypeV2.INBOX:
        markConversationAsUnread({ conversationIdToUpdate: conversationId });
        setIsBlurred(false);
        return;
    }
  }, [folder, conversationId, undoArchiveConversation, undoReportConversation, markConversationAsUnread]);

  const swipeRight: ISwipeActionProps | undefined = useMemo(() => {
    const swipeRightContent = MOBILE_SWIPE_RIGHT_CONTENT_MAP[folder];

    if (!swipeRightContent) {
      return undefined;
    }

    return {
      content: (
        <Box
          css={{
            ...swipeContainerStyles,
            justifyContent: 'start',
            backgroundColor: folder === SatelliteConversationFolderTypeV2.INBOX ? 'green.100' : undefined,
          }}
        >
          {swipeRightContent.icon}
          <Paragraph textStyle="body-xs" css={{ marginLeft: 'tighter' }}>
            {swipeRightContent.text}
          </Paragraph>
        </Box>
      ),
      action: swipeRightAction,
    };
  }, [folder, swipeRightAction]);

  return (
    <SwipeableListItem swipeLeft={swipeLeft} swipeRight={swipeRight}>
      <Box css={{ opacity: isBlurred ? 0.5 : 1, width: '100%' }}>{children}</Box>
    </SwipeableListItem>
  );
}
